import { useTranslation } from "react-i18next";
import { Label, Input } from "@tellescope/ui";
import { Select } from "@tellescope/design-system";
import type { PinnedFormsListBlock } from "../../../../../shared/schema";
import { LayoutSection } from "../shared/LayoutSection";
import { VisibilitySection } from "../shared/VisibilitySection";
import { ItemFilterSection } from "../shared/ItemFilterSection";
import type { BlockTypeEditorProps } from "../types";
import type { VisibilityCondition } from "../../../../../shared/schema/visibility";

type PinnedFormsListBlockEditorProps = BlockTypeEditorProps<PinnedFormsListBlock>;

export function PinnedFormsListBlockEditor({
  block,
  updateProps,
  updateLayout,
  updateVisibility,
  layoutOpen,
  setLayoutOpen,
  visibilityOpen,
  setVisibilityOpen,
}: PinnedFormsListBlockEditorProps) {
  const { t } = useTranslation();

  const updateItemFilter = (condition: VisibilityCondition | undefined) => {
    if (condition) {
      updateProps({ itemFilter: { condition } });
    } else {
      updateProps({ itemFilter: undefined });
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t(
          "Displays all forms with portal submission enabled. Forms are automatically loaded based on their settings."
        )}
      </p>
      <div className="space-y-2">
        <Label>{t("Max Items")}</Label>
        <Select
          value={String(block.props.maxItems ?? 4)}
          onValueChange={(value) => updateProps({ maxItems: parseInt(value) })}
        >
          <Select.Trigger>
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="1">1</Select.Item>
            <Select.Item value="2">2</Select.Item>
            <Select.Item value="3">3</Select.Item>
            <Select.Item value="4">4</Select.Item>
            <Select.Item value="5">5</Select.Item>
            <Select.Item value="10">10</Select.Item>
            <Select.Item value="20">20</Select.Item>
          </Select.Content>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>{t("Title (optional)")}</Label>
        <Input
          value={block.props.title || ""}
          onChange={(e) => updateProps({ title: e.target.value || undefined })}
          placeholder={t("Available Forms")}
        />
      </div>
      <div className="space-y-2">
        <Label>{t("Empty Message (optional)")}</Label>
        <Input
          value={block.props.emptyMessage || ""}
          onChange={(e) => updateProps({ emptyMessage: e.target.value || undefined })}
          placeholder={t("No forms available")}
        />
      </div>

      <ItemFilterSection
        itemFilter={block.props.itemFilter}
        onChange={updateItemFilter}
        description={t(
          "Filter which forms are displayed based on conditions. Use 'Forms' source for form properties, and combine with 'Form Responses' to filter based on submission history."
        )}
      />

      <LayoutSection layout={block.layout} onChange={updateLayout} open={layoutOpen} onOpenChange={setLayoutOpen} />
      <VisibilitySection
        visibility={block.visibility}
        onChange={updateVisibility}
        open={visibilityOpen}
        onOpenChange={setVisibilityOpen}
      />
    </div>
  );
}
